import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, ChevronDown, ChevronUp, Check, Loader, X, ArrowRight, Heart, Camera } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AcompananteProposalModal({ rooms, loading, onEnter, onClose, onJoinWaitingList }) {
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
        <div className="bg-[#FDFAF4] rounded-3xl p-10 flex flex-col items-center gap-3">
          <Loader size={28} className="text-[#E48D57] animate-spin" />
          <p className="text-sm text-[#5B3E2E]/70">Buscando salas disponibles…</p>
        </div>
      </div>
    );
  }

  if (!rooms || rooms.length === 0) {
    return (
      <div data-testid="acompanante-no-rooms-modal" className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[#FDFAF4] rounded-3xl max-w-md w-full p-6 animate-scale-in relative" onClick={e => e.stopPropagation()}>
          <button data-testid="proposal-close-btn" onClick={onClose} className="absolute right-4 top-4 text-[#5B3E2E]/40 hover:text-[#5B3E2E]"><X size={20} /></button>
          <div className="w-14 h-14 rounded-full bg-[#E48D57]/10 flex items-center justify-center mx-auto mb-4">
            <Heart size={26} className="text-[#E48D57]" />
          </div>
          <h3 className="font-heading text-xl text-[#5B3E2E] text-center mb-3">Aún no hay salas disponibles</h3>
          <p className="text-sm text-[#5B3E2E]/65 text-center leading-relaxed mb-5">
            En este momento no hay ninguna sala abierta. ¿Quieres que te avisemos en cuanto aparezca una que coincida con tus temáticas?
          </p>
          <div className="flex flex-col gap-2">
            <Button data-testid="join-waiting-btn" onClick={onJoinWaitingList} className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full py-3 text-sm font-medium">
              Avísame cuando haya una sala
            </Button>
            <Button onClick={onClose} variant="outline" className="w-full rounded-full border-[#5B3E2E]/15 text-[#5B3E2E] py-3 text-sm">
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (index >= rooms.length) {
    return (
      <div data-testid="acompanante-end-modal" className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[#FDFAF4] rounded-3xl max-w-md w-full p-6 animate-scale-in relative" onClick={e => e.stopPropagation()}>
          <button data-testid="proposal-close-btn" onClick={onClose} className="absolute right-4 top-4 text-[#5B3E2E]/40 hover:text-[#5B3E2E]"><X size={20} /></button>
          <div className="w-14 h-14 rounded-full bg-[#6B9070]/10 flex items-center justify-center mx-auto mb-4">
            <Check size={26} className="text-[#6B9070]" />
          </div>
          <h3 className="font-heading text-xl text-[#5B3E2E] text-center mb-3">Has visto todas las salas disponibles</h3>
          <p className="text-sm text-[#5B3E2E]/65 text-center leading-relaxed mb-5">
            Has revisado todas las salas abiertas. Puedes empezar de nuevo, esperar a que aparezca una nueva que coincida contigo o cerrar.
          </p>
          <div className="flex flex-col gap-2">
            <Button data-testid="join-waiting-btn" onClick={onJoinWaitingList} className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full py-3 text-sm font-medium">
              Avísame cuando haya una sala
            </Button>
            <div className="flex gap-2">
              <Button data-testid="proposal-restart-btn" onClick={() => setIndex(0)} variant="outline" className="flex-1 rounded-full border-[#5B3E2E]/15 text-[#5B3E2E]">
                Volver a buscar
              </Button>
              <Button onClick={onClose} variant="outline" className="flex-1 rounded-full border-[#5B3E2E]/15 text-[#5B3E2E]">
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const room = rooms[index];
  const creator = room.acompanado?.username || "Anónimo";
  const topics = room.topics || [];
  const initial = creator.charAt(0).toUpperCase();

  const handleEnter = async () => {
    setSubmitting(true);
    try {
      await onEnter(room);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="acompanante-proposal-modal" className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FDFAF4] rounded-3xl max-w-md w-full p-6 animate-scale-in relative" onClick={e => e.stopPropagation()}>
        <button data-testid="proposal-close-btn" onClick={onClose} className="absolute right-4 top-4 text-[#5B3E2E]/40 hover:text-[#5B3E2E]">
          <X size={20} />
        </button>

        <p className="text-[10px] tracking-[0.2em] uppercase text-[#5B3E2E]/40 font-semibold text-center mb-3">
          Sala {index + 1} de {rooms.length}
        </p>

        <div className="flex items-center justify-center mb-3">
          <div className="w-14 h-14 rounded-full bg-[#6B9070] flex items-center justify-center text-white text-lg font-semibold">
            {initial}
          </div>
        </div>

        <p className="text-center text-xs text-[#5B3E2E]/50 mb-1">Sala creada por</p>
        <h3 data-testid="proposal-creator" className="font-heading text-xl text-[#5B3E2E] text-center mb-4">{creator}</h3>

        {room.room_name && (
          <p className="text-sm text-[#5B3E2E]/70 text-center italic mb-4">"{room.room_name}"</p>
        )}

        <div className="mb-5">
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#5B3E2E]/45 font-semibold mb-2 text-center">Temáticas</p>
          <div data-testid="proposal-topics" className="flex flex-wrap gap-2 justify-center">
            {topics.length === 0 ? (
              <span className="text-xs text-[#5B3E2E]/40">Sin temáticas indicadas</span>
            ) : topics.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full text-xs bg-[#E48D57]/10 text-[#E48D57] font-medium">
                {t}
              </span>
            ))}
          </div>
        </div>

        <p className="text-sm text-[#5B3E2E]/70 text-center leading-relaxed mb-5">
          ¿Quieres entrar en esta sala como acompañante?
        </p>

        <div className="flex flex-col gap-2">
          <Button
            data-testid="proposal-enter-btn"
            onClick={handleEnter}
            disabled={submitting}
            className="w-full bg-[#6B9070] hover:bg-[#5A8060] text-white rounded-full py-3 font-medium flex items-center justify-center gap-2"
          >
            {submitting ? <Loader size={16} className="animate-spin" /> : null}
            {submitting ? "Entrando…" : "Entrar"}
          </Button>
          <Button
            data-testid="proposal-next-btn"
            onClick={() => setIndex(index + 1)}
            disabled={submitting}
            variant="outline"
            className="w-full rounded-full border-[#5B3E2E]/15 text-[#5B3E2E] py-3 font-medium flex items-center justify-center gap-2"
          >
            Buscar otra sala
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function JoinRoomPage() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [role, setRole] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [roomName, setRoomName] = useState("");
  const [roomImage, setRoomImage] = useState(null);
  const [roomImagePreview, setRoomImagePreview] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [showProposal, setShowProposal] = useState(false);
  const [availableRooms, setAvailableRooms] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const roomNameInputRef = useRef(null);
  const roomNameSectionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/topics`, { withCredentials: true }).then(r => setTopics(r.data)).catch(() => {});
  }, []);

  const MAX_TOPICS = 2; // Acompañado uses this; acompañante doesn't pick topics anymore.

  const toggleTopic = (topic) => {
    setSelectedTopics(prev => {
      if (prev.includes(topic)) return prev.filter(t => t !== topic);
      if (prev.length >= MAX_TOPICS) { toast.error(`Máximo ${MAX_TOPICS} temáticas`); return prev; }
      const next = [...prev, topic];
      // When the user reaches the max, jump to the room-name section.
      if (next.length === MAX_TOPICS) {
        setTimeout(() => {
          roomNameSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          roomNameInputRef.current?.focus();
        }, 250);
      }
      return next;
    });
  };

  const handleRoomImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRoomImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setRoomImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeRoomImage = () => {
    setRoomImage(null);
    setRoomImagePreview(null);
  };

  const [acompananteTopics, setAcompananteTopics] = useState([]); // empty = "Todas". Stored as "catId::sub" keys internally.
  const [allTopicsToggle, setAllTopicsToggle] = useState(true);
  const [waitingMode, setWaitingMode] = useState(false);

  // Helpers to namespace topic keys (avoids collisions when the same sub name appears in several categories,
  // e.g. "Otra", "Distanciamiento", "Pareja").
  const keyOf = (catId, sub) => `${catId}::${sub}`;
  // Convert namespaced keys → bare sub names (deduped) for API / room-overlap comparisons.
  const bareTopics = (keys) => Array.from(new Set(keys.map(k => k.includes("::") ? k.split("::")[1] : k)));

  // Load saved preferences when role becomes acompañante
  useEffect(() => {
    if (role !== "acompanante") return;
    if (topics.length === 0) return; // wait until categories are loaded so we can re-namespace
    axios.get(`${API}/users/me/acompanante-preferences`, { withCredentials: true })
      .then(r => {
        const saved = r.data?.topics || [];
        if (saved.length === 0) {
          setAcompananteTopics([]);
          setAllTopicsToggle(true);
          return;
        }
        // Re-attach each saved bare name to every category that contains it.
        const keys = [];
        for (const cat of topics) {
          for (const sub of (cat.subcategories || [])) {
            if (saved.includes(sub)) keys.push(keyOf(cat.id, sub));
          }
        }
        setAcompananteTopics(keys);
        setAllTopicsToggle(false);
      })
      .catch(() => {});
  }, [role, topics]);

  const toggleAcompananteTopic = (catId, sub) => {
    const k = keyOf(catId, sub);
    setAllTopicsToggle(false);
    setAcompananteTopics(prev => prev.includes(k) ? prev.filter(t => t !== k) : [...prev, k]);
  };
  const handleSelectAllTopics = () => {
    setAllTopicsToggle(true);
    setAcompananteTopics([]);
  };
  const isCategoryFullySelected = (cat) => {
    const subs = cat.subcategories || [];
    return subs.length > 0 && subs.every(s => acompananteTopics.includes(keyOf(cat.id, s)));
  };
  const isCategoryPartiallySelected = (cat) => {
    const subs = cat.subcategories || [];
    const some = subs.some(s => acompananteTopics.includes(keyOf(cat.id, s)));
    const all = subs.every(s => acompananteTopics.includes(keyOf(cat.id, s)));
    return some && !all;
  };
  const toggleAcompananteCategory = (cat) => {
    setAllTopicsToggle(false);
    const subs = cat.subcategories || [];
    const keys = subs.map(s => keyOf(cat.id, s));
    setAcompananteTopics(prev => {
      const all = keys.every(k => prev.includes(k));
      if (all) return prev.filter(t => !keys.includes(t));
      const set = new Set(prev);
      keys.forEach(k => set.add(k));
      return Array.from(set);
    });
  };

  const startAcompananteSearch = async () => {
    setShowProposal(true);
    setLoadingRooms(true);
    setAvailableRooms(null);
    try {
      const flatTopics = bareTopics(acompananteTopics);
      // Save preferences first (server uses flat names)
      await axios.put(`${API}/users/me/acompanante-preferences`, { topics: flatTopics }, { withCredentials: true });
      const { data } = await axios.get(`${API}/rooms/available-for-acompanante`, { withCredentials: true });
      // Sort client-side by topic overlap with prefs (server already returns by created_at).
      const prefSet = new Set(flatTopics);
      const sorted = (data || []).slice().sort((a, b) => {
        const oa = (a.topics || []).filter(t => prefSet.has(t)).length;
        const ob = (b.topics || []).filter(t => prefSet.has(t)).length;
        if (oa !== ob) return ob - oa;
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
      setAvailableRooms(sorted);
    } catch (err) {
      toast.error("No se pudieron cargar las salas");
      setAvailableRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleJoinWaitingList = async () => {
    try {
      await axios.post(`${API}/rooms/join`, {
        role: "acompanante",
        preferred_topics: bareTopics(acompananteTopics),
        join_waiting_list: true,
      }, { withCredentials: true });
      setWaitingMode(true);
      setShowProposal(false);
      toast.success("Te avisaremos en cuanto haya una sala");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo añadir a la lista de espera");
    }
  };

  const handleSelectRole = (selectedRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleAcompananteEnter = async (room) => {
    try {
      await axios.post(`${API}/rooms/${room.id}/join-as-acompanante`, {}, { withCredentials: true });
      toast.success("Te has unido a la sala");
      navigate(`/app/salas/${room.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al unirse");
    }
  };

  const handleSubmitAcompanado = async () => {
    if (selectedTopics.length === 0) return;
    if (!roomName.trim()) { toast.error("Pon un nombre a tu sala"); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rooms/join`, { role: "acompanado", topics: selectedTopics, room_name: roomName.trim() }, { withCredentials: true });
      // Optionally upload room image right after creation.
      if (roomImage) {
        try {
          const formData = new FormData();
          formData.append("file", roomImage);
          await axios.post(`${API}/rooms/${data.id}/image`, formData, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (_) {
          toast.error("La sala se creó pero no se pudo subir la imagen");
        }
      }
      toast.success("Sala creada");
      navigate(`/app/salas/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear sala");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="join-room-page" className="max-w-lg mx-auto px-4 py-6">
      <button data-testid="join-back-btn" onClick={() => navigate("/app/salas")} className="flex items-center gap-2 text-[#5B3E2E]/60 hover:text-[#5B3E2E] mb-6">
        <ArrowLeft size={18} />
        <span className="text-sm">Volver a salas</span>
      </button>

      <h1 className="font-heading text-2xl text-[#5B3E2E] mb-2">Unirse a una sala</h1>
      <p className="text-sm text-[#5B3E2E]/60 mb-6">Selecciona tu rol para empezar</p>

      {/* Step 1: Role */}
      <div className="mb-6 animate-fade-in">
        <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Paso 1: Tu rol</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="role-acompanado"
            onClick={() => handleSelectRole("acompanado")}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${
              role === "acompanado"
                ? "border-[#6B9070] bg-[#6B9070]/5"
                : "border-[#5B3E2E]/10 bg-[#FDFAF4] hover:border-[#5B3E2E]/20"
            }`}
          >
            <div className={`w-8 h-8 rounded-full mb-2 flex items-center justify-center ${role === "acompanado" ? "bg-[#6B9070]" : "bg-[#6B9070]/20"}`}>
              <span className="text-white text-sm">A</span>
            </div>
            <p className="font-medium text-[#5B3E2E] text-sm">Ser acompañad@</p>
            <p className="text-xs text-[#5B3E2E]/50 mt-1">Comparte y recibe apoyo</p>
          </button>
          <button
            data-testid="role-acompanante"
            onClick={() => handleSelectRole("acompanante")}
            className={`p-4 rounded-2xl border-2 text-left transition-all ${
              role === "acompanante"
                ? "border-[#E48D57] bg-[#E48D57]/5"
                : "border-[#5B3E2E]/10 bg-[#FDFAF4] hover:border-[#5B3E2E]/20"
            }`}
          >
            <div className={`w-8 h-8 rounded-full mb-2 flex items-center justify-center ${role === "acompanante" ? "bg-[#E48D57]" : "bg-[#E48D57]/20"}`}>
              <span className="text-white text-sm">C</span>
            </div>
            <p className="font-medium text-[#5B3E2E] text-sm">Ser acompañante</p>
            <p className="text-xs text-[#5B3E2E]/50 mt-1">Escucha y acompaña</p>
          </button>
        </div>
      </div>

      {/* Step 2: Acompañante - topic preferences */}
      {step >= 2 && role === "acompanante" && !waitingMode && (
        <div className="mb-6 animate-slide-up">
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Paso 2: Tus temáticas (opcional)</p>
          <p className="text-xs text-[#5B3E2E]/55 mb-4 leading-relaxed">
            Elige las temáticas en las que tienes más interés o experiencia. La app dará prioridad a salas que coincidan, pero no te excluirá de otras si no aparecen coincidencias.
          </p>

          {/* Todas toggle */}
          <button
            data-testid="topic-toggle-all"
            onClick={handleSelectAllTopics}
            className={`w-full flex items-center justify-between p-3 rounded-2xl mb-3 transition-all border-2 ${
              allTopicsToggle ? "border-[#E48D57] bg-[#E48D57]/8" : "border-[#5B3E2E]/10 bg-[#FDFAF4]"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${allTopicsToggle ? "bg-[#E48D57] text-white" : "bg-[#5B3E2E]/10 text-[#5B3E2E]/40"}`}>
                <Check size={14} />
              </div>
              <div className="text-left">
                <p className="font-medium text-[#5B3E2E] text-sm">Todas las temáticas</p>
                <p className="text-[10px] text-[#5B3E2E]/45">Acompañar en cualquier tema</p>
              </div>
            </div>
          </button>

          {/* Topic categories - sin límite */}
          <p className="text-[11px] text-[#5B3E2E]/40 mb-2 italic">O elige tus temáticas preferidas (sin límite). Puedes marcar un apartado entero o subtemáticas concretas:</p>
          <div className="space-y-2">
            {topics.map((cat) => {
              const fully = isCategoryFullySelected(cat);
              const partial = isCategoryPartiallySelected(cat);
              return (
                <div key={cat.id} className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 overflow-hidden">
                  <div className="flex items-stretch">
                    <button
                      data-testid={`acomp-cat-toggle-${cat.id}`}
                      onClick={() => toggleAcompananteCategory(cat)}
                      className="flex items-center gap-3 px-3 py-3 flex-1 text-left hover:bg-[#5B3E2E]/5 transition-colors"
                    >
                      <div
                        className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all border-2 ${
                          fully
                            ? "bg-[#E48D57] border-[#E48D57] text-white"
                            : partial
                              ? "bg-[#E48D57]/20 border-[#E48D57] text-[#E48D57]"
                              : "bg-white border-[#5B3E2E]/25 text-transparent"
                        }`}
                      >
                        {fully ? <Check size={13} /> : partial ? <span className="block w-2 h-0.5 bg-[#E48D57] rounded" /> : null}
                      </div>
                      <span className="font-medium text-[#5B3E2E] text-sm">{cat.name}</span>
                    </button>
                    <button
                      data-testid={`acomp-cat-expand-${cat.id}`}
                      onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                      aria-label="Expandir subtemáticas"
                      className="px-3 flex items-center text-[#5B3E2E]/40 hover:text-[#5B3E2E] hover:bg-[#5B3E2E]/5 transition-colors border-l border-[#5B3E2E]/8"
                    >
                      {expandedCat === cat.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                  {expandedCat === cat.id && (
                    <div className="px-3 pb-3 flex flex-wrap gap-2 border-t border-[#5B3E2E]/5 pt-3">
                      {cat.subcategories.map((sub) => {
                        const isSelected = !allTopicsToggle && acompananteTopics.includes(keyOf(cat.id, sub));
                        return (
                          <button
                            key={sub}
                            data-testid={`acomp-topic-${sub.replace(/\s/g, "-")}`}
                            onClick={() => toggleAcompananteTopic(cat.id, sub)}
                            className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                              isSelected
                                ? "bg-[#E48D57] text-white"
                                : "bg-[#5B3E2E]/5 text-[#5B3E2E]/70 hover:bg-[#5B3E2E]/10"
                            }`}
                          >
                            {isSelected && <Check size={12} className="inline mr-1" />}
                            {sub}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Button
            data-testid="search-acomp-rooms-btn"
            onClick={startAcompananteSearch}
            className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium mt-5 flex items-center justify-center gap-2"
          >
            Buscar salas para acompañar
          </Button>
        </div>
      )}

      {/* Acompañante: waiting state */}
      {role === "acompanante" && waitingMode && (
        <div data-testid="acompanante-waiting-state" className="bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/10 p-6 text-center mb-4 animate-fade-in">
          <div className="w-14 h-14 rounded-full bg-[#6B9070]/10 flex items-center justify-center mx-auto mb-3">
            <Loader size={26} className="text-[#6B9070] animate-spin" />
          </div>
          <p className="font-heading text-lg text-[#5B3E2E] mb-2">Estás en la lista de espera</p>
          <p className="text-sm text-[#5B3E2E]/60 leading-relaxed mb-4">
            Te avisaremos en cuanto aparezca una sala que coincida con tus temáticas. Mientras tanto, puedes seguir usando la app con normalidad.
          </p>
          <Button
            onClick={() => navigate("/app/salas")}
            className="bg-[#5B3E2E] hover:bg-[#4a3326] text-white rounded-full px-6 py-2.5 text-sm"
          >
            Volver a Salas
          </Button>
        </div>
      )}

      {/* Step 2: Topics (acompañado only) */}
      {step >= 2 && role === "acompanado" && (
        <div className="mb-6 animate-slide-up">
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">
            Paso 2: Temáticas ({selectedTopics.length}/{MAX_TOPICS} seleccionadas)
          </p>
          <div className="space-y-2">
            {topics.map((cat) => (
              <div key={cat.id} className="bg-[#FDFAF4] rounded-2xl border border-[#5B3E2E]/10 overflow-hidden">
                <button
                  data-testid={`category-${cat.id}`}
                  onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="font-medium text-[#5B3E2E] text-sm">{cat.name}</span>
                  {expandedCat === cat.id ? <ChevronUp size={16} className="text-[#5B3E2E]/40" /> : <ChevronDown size={16} className="text-[#5B3E2E]/40" />}
                </button>
                {expandedCat === cat.id && (
                  <div className="px-4 pb-4 flex flex-wrap gap-2">
                    {cat.subcategories.map((sub) => {
                      const isSelected = selectedTopics.includes(sub);
                      return (
                        <button
                          key={sub}
                          data-testid={`topic-${sub.replace(/\s/g, "-")}`}
                          onClick={() => toggleTopic(sub)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                            isSelected
                              ? "bg-[#E48D57] text-white"
                              : "bg-[#5B3E2E]/5 text-[#5B3E2E]/70 hover:bg-[#5B3E2E]/10"
                          }`}
                        >
                          {isSelected && <Check size={12} className="inline mr-1" />}
                          {sub}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Room Name + optional image (acompañado only) */}
      {step >= 2 && role === "acompanado" && selectedTopics.length > 0 && (
        <div ref={roomNameSectionRef} className="mb-6 animate-slide-up">
          <p className="text-xs tracking-[0.15em] uppercase text-[#5B3E2E]/50 font-semibold mb-3">Paso 3: Nombre y foto de tu sala</p>

          <div className="flex gap-3 items-start">
            {/* Optional image picker */}
            <label data-testid="room-image-picker-label" className="relative flex-shrink-0 w-16 h-16 rounded-2xl bg-[#FDFAF4] border-2 border-dashed border-[#5B3E2E]/20 flex items-center justify-center cursor-pointer hover:border-[#E48D57] hover:bg-[#E48D57]/5 transition-colors overflow-hidden">
              {roomImagePreview ? (
                <>
                  <img src={roomImagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeRoomImage(); }}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                    aria-label="Quitar imagen"
                  >
                    <X size={11} />
                  </button>
                </>
              ) : (
                <Camera size={20} className="text-[#5B3E2E]/40" />
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleRoomImageChange}
                data-testid="room-image-picker"
              />
            </label>

            <div className="flex-1 min-w-0">
              <Input
                ref={roomNameInputRef}
                data-testid="room-name-input"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="Ej: Mi espacio para hablar de..."
                className="bg-[#FDFAF4] border-[#5B3E2E]/15 rounded-xl text-[#5B3E2E] text-sm h-11"
              />
              <p className="text-[10px] text-[#5B3E2E]/40 mt-1.5">La foto es opcional · puedes cambiarla luego</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit (acompañado only) */}
      {role === "acompanado" && selectedTopics.length > 0 && roomName.trim() && (
        <Button
          data-testid="submit-join-room"
          onClick={handleSubmitAcompanado}
          disabled={submitting}
          className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium transition-all flex items-center justify-center gap-2"
        >
          {submitting ? <Loader size={16} className="animate-spin" /> : null}
          {submitting ? "Creando sala..." : "Crear mi sala"}
        </Button>
      )}

      {/* Proposal modal */}
      {showProposal && (
        <AcompananteProposalModal
          rooms={availableRooms}
          loading={loadingRooms}
          onEnter={handleAcompananteEnter}
          onClose={() => setShowProposal(false)}
          onJoinWaitingList={handleJoinWaitingList}
        />
      )}
    </div>
  );
}
